// Layout state machinery: per-tab seed templates, the persisted-blob
// serializer/normalizer (localStorage + server), drag reordering, and
// widget visibility filters.
import { SCOPE_TABS, WIDGETS, packLayoutForWidgets } from '../widgetRegistry'

// ---- templates ----
// Seed state for the fixed scope tabs: each tab's layout is generated
// from WIDGETS filtered by scope and packed via packLayoutForWidgets;
// non-Overview tabs also carry a pickedEntity.

// Pack only the widgets whose `scopedBy` contains the tab key. Widgets
// without a `scopedBy` array, or with one that doesn't include the
// tab, are excluded so each tab opens with a focused starter layout.
const buildLayoutForTab = (tabKey) => {
  const widgets = WIDGETS.filter(
    (widget) => Array.isArray(widget.scopedBy) && widget.scopedBy.includes(tabKey)
  )
  return packLayoutForWidgets(widgets)
}

// Initial per-tab state. `pickedEntity` is null until the picker on
// that tab selects an entity (auto-selected to the first option on
// mount by each picker component). Overview has no picker so the
// field is absent there.
const buildTabState = (tabKey) => {
  const base = { layout: buildLayoutForTab(tabKey) }
  if (tabKey === 'overview') {
    return base
  }
  return { ...base, pickedEntity: null }
}

// Fresh seed for first-time users (and the layout serializer's
// re-seed-on-corrupt fallback).
export const SEED_STATE = () => ({
  activeTab: 'overview',
  tabs: Object.fromEntries(SCOPE_TABS.map((tab) => [tab.key, buildTabState(tab.key)])),
})

// Re-seed just one tab's layout — used by the "Reset" button on a tab.
export const buildSeedTab = (tabKey) => buildTabState(tabKey)

// ---- layoutSerializer ----
// Persists per-tab state per user. localStorage stand-in for the
// eventual server-side persistence. The key is versioned so breaking
// changes can re-seed without crashing existing users.

// Version stamp for the persisted blob (localStorage key AND the
// server-side dashboard_settings row); mismatched blobs are discarded
// for a fresh seed. (v12–v33 bump history: see git log.)
//
// ⚠️  A bump is a WIPE, not a migration — it discards EVERY user's
// saved layout across localStorage AND the DB. normalizeState()
// already handles widgets being added/removed and new tabs WITHOUT a
// bump; bump only for a genuine blob-shape change, and prefer writing
// a migration in normalizeState instead. Widget ids must stay stable
// (a rename resets that widget's saved position/size).
export const STATE_VERSION = 33

export const STATE_KEY = (userId) => `qa-dashboard:state:v${STATE_VERSION}:${userId || 'anon'}`

// Widgets whose registry `defaultLayout` changed after users already
// had a saved blob. A stored entry keeps whatever size it was saved
// with, so without this the new default only reaches new users and tab
// resets — and a STATE_VERSION bump would wipe every widget's position
// to move one. Record the size the widget USED to default to; only
// entries still sitting on it move, so a size the user chose is theirs
// to keep.
const SUPERSEDED_DEFAULTS = [
  // flagged_criteria became the five-column "Flagged Agents per
  // Criteria" table; a half-width cell has to drop the scorecard
  // column to fit it.
  { id: 'flagged_criteria', previousDefault: { w: 6, h: 14 } },
]

const migrateEntrySize = (entry) => {
  const superseded = SUPERSEDED_DEFAULTS.find((candidate) => candidate.id === entry.id)
  if (!superseded) {
    return entry
  }
  const { w, h } = entry.layout || {}
  if (w !== superseded.previousDefault.w || h !== superseded.previousDefault.h) {
    return entry
  }
  const currentDefault = WIDGETS.find((widget) => widget.id === entry.id)?.defaultLayout
  if (!currentDefault) {
    return entry
  }
  // x/y stay as they were: the bento grid places densely and reads only
  // w/h off the entry, so a stale column offset is inert.
  return { ...entry, layout: { ...entry.layout, ...currentDefault } }
}

// Normalize an untrusted parsed blob (localStorage or server) into a
// valid state shape: re-seed missing tabs, drop unknown widget ids,
// append widgets registered after the blob was saved, and migrate
// entries left on a superseded default size. Returns null when the blob
// is unusable so callers can fall back to the seed.
export const normalizeState = (parsed) => {
  if (!parsed?.tabs || typeof parsed.tabs !== 'object') {
    return null
  }
  const seed = SEED_STATE()
  const validTabKeys = new Set(SCOPE_TABS.map((tab) => tab.key))
  const knownIds = new Set(WIDGETS.map((widget) => widget.id))
  // Merge tab-by-tab so a partially-saved blob still re-seeds the
  // missing tabs instead of leaving holes in the UI.
  const tabs = {}
  SCOPE_TABS.forEach((tab) => {
    const stored = parsed.tabs[tab.key]
    if (!stored || !Array.isArray(stored.layout)) {
      tabs[tab.key] = seed.tabs[tab.key]
      return
    }
    // Drop unknown ids AND append any new ids the seed has that
    // the stored blob doesn't (i.e. widgets registered after the
    // saved state). Otherwise registering a new widget would only
    // show up after another STATE_KEY bump.
    const storedById = new Map(stored.layout.map((entry) => [entry.id, entry]))
    const filteredStored = stored.layout
      .filter((entry) => knownIds.has(entry.id))
      .map(migrateEntrySize)
    const additions = seed.tabs[tab.key].layout.filter((entry) => !storedById.has(entry.id))
    tabs[tab.key] = {
      ...seed.tabs[tab.key],
      ...stored,
      layout: [...filteredStored, ...additions],
    }
  })
  const activeTab = validTabKeys.has(parsed.activeTab) ? parsed.activeTab : seed.activeTab
  return { activeTab, tabs }
}

export const loadState = (
  userId,
  storage = typeof localStorage !== 'undefined' ? localStorage : null
) => {
  if (!storage) {
    return SEED_STATE()
  }
  try {
    const raw = storage.getItem(STATE_KEY(userId))
    if (!raw) {
      return SEED_STATE()
    }
    return normalizeState(JSON.parse(raw)) || SEED_STATE()
  } catch {
    return SEED_STATE()
  }
}

export const saveState = (
  userId,
  state,
  storage = typeof localStorage !== 'undefined' ? localStorage : null
) => {
  if (!storage) {
    return
  }
  try {
    storage.setItem(STATE_KEY(userId), JSON.stringify(state))
  } catch {
    /* storage may be full or disabled; re-seed next load */
  }
}

// ---- reorderLayout ----
// Pure array-move for the per-tab layout list. Render order within a
// band is array order, so "drag widget A onto widget B" moves A's
// entry next to B's. Persistence is free -- the caller already saves
// the layout array on every state change.

// Move the entry with `dragId` to sit next to the entry with
// `targetId`, on the far side relative to the drag direction: a
// forward drag (dragId before targetId) lands it AFTER the target, a
// backward drag lands it BEFORE. Returns the original array (same
// reference) when the move is a no-op (unknown ids, self-drop) so
// React state setters can skip a re-render.
export const moveEntryToTarget = (layout, dragId, targetId) => {
  if (!Array.isArray(layout) || dragId === targetId) {
    return layout
  }
  const from = layout.findIndex((entry) => entry.id === dragId)
  const to = layout.findIndex((entry) => entry.id === targetId)
  if (from === -1 || to === -1) {
    return layout
  }
  const next = [...layout]
  const [moved] = next.splice(from, 1)
  // The drop lands the widget on the far side of the target relative
  // to where it was dragged from: a forward drag (from before the
  // target) drops it AFTER, a backward drag drops it BEFORE. Inserting
  // only "before" made a forward drop onto the very next widget a
  // no-op -- the array order came out identical and the dense grid
  // re-flowed to the same picture, so the widgets appeared stuck.
  // Recompute the target index against the spliced array.
  const targetIndex = next.findIndex((entry) => entry.id === targetId)
  const insertAt = from < to ? targetIndex + 1 : targetIndex
  next.splice(insertAt, 0, moved)
  return next
}

// ---- widgetVisibility ----
// Single predicate for "should this widget render?": entry.visible
// (persisted per tab), widget.hiddenInAgentMode (view-as), and
// unknown-id drop. ctx: { inViewAs, activeTab }.

export const isWidgetVisible = (widget, entry, ctx = {}) => {
  if (!widget) {
    return false
  }
  if (entry && entry.visible === false) {
    return false
  }
  if (ctx.inViewAs && widget.hiddenInAgentMode) {
    return false
  }
  return true
}

// Filter a layout's entries down to the ones visible under `ctx`.
// Preserves order. `getWidget` is `(id) => WIDGET_BY_ID[id]`, passed
// in so this module doesn't import the registry (keeps it pure +
// trivially testable).
export const filterVisibleEntries = (entries, getWidget, ctx = {}) => {
  if (!Array.isArray(entries)) {
    return []
  }
  return entries.filter((entry) => isWidgetVisible(getWidget(entry.id), entry, ctx))
}

// Pure state transition: flip `visible` on one entry in one tab.
// Returns a new top-level dashboard state. No-op (returns prev state
// reference) if the tab doesn't exist or the widget isn't in its
// layout -- keeps React from triggering a needless re-render.
export const setLayoutEntryVisibility = (state, tabKey, widgetId, visible) => {
  const tab = state?.tabs?.[tabKey]
  if (!tab || !Array.isArray(tab.layout)) {
    return state
  }
  let changed = false
  const layout = tab.layout.map((entry) => {
    if (entry.id !== widgetId) {
      return entry
    }
    if (entry.visible === visible) {
      return entry
    }
    changed = true
    return { ...entry, visible }
  })
  if (!changed) {
    return state
  }
  return {
    ...state,
    tabs: { ...state.tabs, [tabKey]: { ...tab, layout } },
  }
}
